import React, { useEffect, useState } from "react";
import "./Home.css";
import styles from "./Frontdesk/Frontdesk.module.css";
import Header from "../header/Header";
import SideBarLeft from "../sideBar/SideBarLeft";
import SideBarRight from "../sideBar/SideBarRight";
import HospitalityManagement from "../hms/HospitalityManagement";
import Settup from "../Settup/Settup";
import InternalCommunication from "../Communication/InternalCommunication/InternalMessage";
import AppAnalysis from "./Analysis/Analysis";
import useAuth from "../../hooks/useAuth";
import webSocketManager from "../../suckets/WebSocketManager";
import { SocketUrls } from "../../constants/baseurl";
import CRM from "../CRM/CRM";
import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
  DrawerProps,
  Button,
} from "@fluentui/react-components";
import { Dismiss24Regular, PersonAdd16Regular, Search16Regular } from "@fluentui/react-icons";
import Email from "../Email/Email";
import Reatils from "../Retails/Retail";
import Accounting from "../Accounting/Accounting";
import TaskManager from "../TaskManager/TaskManager";
import CilpronCalenders from "../Calender/CilpronCalenders";
import SubscriptionGuard from "../../SubscriptionGuard/SubscriptionGuard";
import Frontdesk from "./Frontdesk/Frontdesk";
import FinanceAndOperation from "../FinanceAndOperation/FinanceAndOperation";
import { HyperView } from "../HyperView/Hyperview";
import HeaderPages, { HEADER_PAGE_PATHS, HeaderPageKey } from "../header/Headerpages";
import SearchResultsPage from "../Global/Searchresultspage";
import type { SearchAppId, SearchResult, SearchTarget } from "../Global/Searchtypes";

const ADMIN_CONTACT = "support@cilpron.com";
const BILLING_URL = "/billing";

// 🔧 DEV/TEST OVERRIDES
const SUBSCRIPTION_OVERRIDES: Record<string, boolean | undefined> = {
  Hospitality: true,
  Settings: true,
  Calender: true,
  CRM: true,
  Retail: true,
  Accounting: true,
  Finance: true,
  Task: true,
  Todo: true,
  email: true,
  HyperView: true,
};

const sampleContacts = [
  { id: "1", name: "Kevin Sturgis", role: "Manager", company: "UBA Group", initials: "KS", status: "Active" },
  { id: "2", name: "Sarah Nwokedi", role: "Supervisor", company: "Zenith Bank", initials: "SN", status: "Active" },
  { id: "3", name: "Chinedu Okeke", role: "Team Lead", company: "Access Bank", initials: "CO", status: "Lead" },
  { id: "4", name: "Ngozi Achumba", role: "Analyst", company: "GTBank", initials: "NA", status: "Active" },
  { id: "5", name: "Tunde Afolabi", role: "Product Manager", company: "Flutterwave", initials: "TA", status: "Lead" },
];

const Home: React.FC = () => {
  const [activeTab, setActiveTab] = useState("hospitality");
  const [support, setSupport] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isFrontdeskOpen, setFrontdeskIsOpen] = useState(false);
  const [position, setPosition] = useState<Required<DrawerProps>["position"]>("end");

  // Contacts drawer state (must be inside the component)
  const [contactSearch, setContactSearch] = useState("");
  const [contactFilter, setContactFilter] = useState("All");
  const [isContactFormVisible, setIsContactFormVisible] = useState(false);

  const [headerPage, setHeaderPage] = useState<HeaderPageKey>("profile");
  // Which Settings screen to open (the nonce lets the same screen be opened twice).
  const [setupRequest, setSetupRequest] = useState<{ value: string; n: number } | undefined>(undefined);
  // The app the user was in when they opened the search page.
  const [searchOrigin, setSearchOrigin] = useState<SearchAppId | null>(null);

  const auth = useAuth();
  const { user } = auth;

  const hasSubscription = (moduleName: string): boolean => {
    if (process.env.NODE_ENV === "development") {
      const override = SUBSCRIPTION_OVERRIDES[moduleName];
      if (override !== undefined) return override;
    }
    return (
      user?.subscription?.modules?.some(
        (m) => m.moduleName === moduleName && m.isActive
      ) ?? false
    );
  };

  const goBack = () => setActiveTab("hospitality");

  // Which app the user is in right now, so search can look there first.
  const TAB_APP: Record<string, SearchAppId> = {
    hospitality: "hospitality",
    CRM: "crm",
    Retail: "retail",
    Accounting: "accounting",
    Finance: "finance",
    Task: "task",
    Todo: "task",
    email: "email",
    Calender: "calendar",
    settings: "settings",
    headerPages: "settings",
  };
  const appFromTab: SearchAppId | null = isFrontdeskOpen
    ? "calltracker"
    : support
    ? "chat"
    : TAB_APP[activeTab] ?? null;
  const currentApp: SearchAppId | null = activeTab === "search" ? searchOrigin : appFromTab;

  const openSearchPage = () => {
    if (activeTab !== "search") setSearchOrigin(appFromTab);
    setFrontdeskIsOpen(false);
    setActiveTab("search");
  };

  const openSearchTarget = (t: SearchTarget) => {
    if (t.headerPage) {
      setHeaderPage(t.headerPage);
      setActiveTab("headerPages");
    } else if (t.tab === "settings") {
      if (t.settupValue) setSetupRequest({ value: t.settupValue, n: Date.now() });
      setActiveTab("settings");
    } else if (t.tab === "calltracker") {
      setFrontdeskIsOpen(true);
    } else if (t.tab === "chat") {
      setSupport(true);
    } else {
      setActiveTab(t.tab);
    }
  };

  // Header menus pass a path; open the matching page inside the app instead of leaving it.
  const openHeaderPath = (path: string) => {
    const key = HEADER_PAGE_PATHS[path];
    if (key) {
      setHeaderPage(key);
      setActiveTab("headerPages");
    } else {
      window.location.assign(path);
    }
  };
  const goToBilling = () => window.open(BILLING_URL, "_blank");

  const filteredContacts = sampleContacts.filter((c) => {
    const q = contactSearch.toLowerCase();
    const matchesSearch =
      !q ||
      c.name.toLowerCase().includes(q) ||
      c.company.toLowerCase().includes(q) ||
      c.role.toLowerCase().includes(q);
    const matchesFilter =
      contactFilter === "All" ||
      (contactFilter === "Active" && c.status === "Active") ||
      (contactFilter === "Leads" && c.status === "Lead") ||
      (contactFilter === "Clients" && c.status === "Active");
    return matchesSearch && matchesFilter;
  });

  const onSelectContact = (contact: typeof sampleContacts[0]) => {
    console.log("Selected contact:", contact);
    // Add your contact selection logic here
  };

  // WebSocket initialisation
  useEffect(() => {
    if (user?.phone && user?.token) {
      webSocketManager.initialize({
        url: SocketUrls.messageInstances,
        id: user.phone,
      });

      const handleConnect = () => console.log("💬 Chat WebSocket connected");
      const handleDisconnect = () => console.log("💬 Chat WebSocket disconnected");

      webSocketManager.on("connect", handleConnect);
      webSocketManager.on("disconnect", handleDisconnect);

      return () => {
        webSocketManager.off("connect", handleConnect);
        webSocketManager.off("disconnect", handleDisconnect);
        webSocketManager.close();
      };
    }
  }, [user?.phone, user?.token]);

 const handleTabClick = (tab: any) => {
  console.log("Tab clicked:", tab);
  if (tab === "support") {
    console.log("Support tab clicked");
    setSupport(true);
    return;
  }
  if (tab === true) {
    setPosition("end");
    setFrontdeskIsOpen(tab);
  } else {
    setActiveTab(tab);
  }
};

console.log("Home render → support:", support, "| IM is NEW version:", String(InternalCommunication).includes("innerOpen"));

  return (
    <>
      <div id="capp" className="capp cpp-fluent-uitheme">
        <div className="cp-homeb83b">
          <div className="QdrUx body-157">
            <Header
              onAppClick={() => setIsDrawerOpen((o) => !o)}
              onHyperView={() => handleTabClick("HyperView")}
              onNavigate={openHeaderPath}
              currentApp={currentApp}
              onOpenResult={(t) => openSearchTarget(t)}
              onSeeAll={openSearchPage}
            />

            <div className="Flvp1">
              <div id="mainApp" className="NzIuH zloVf">
                <SideBarLeft handleTabClick={handleTabClick} />

                {/* ── Hospitality ── */}
                {activeTab === "hospitality" && (
                  <SubscriptionGuard
                    moduleName="Hospitality"
                    isSubscribed={hasSubscription("Hospitality")}
                    onGoBack={goBack}
                    onUpgrade={goToBilling}
                    adminContact={ADMIN_CONTACT}
                  >
                    <HospitalityManagement />
                  </SubscriptionGuard>
                )}

                {/* ── Settings ── */}
                {activeTab === "settings" && (
                  <SubscriptionGuard
                    moduleName="Settings"
                    isSubscribed={hasSubscription("Settings")}
                    onGoBack={goBack}
                    onUpgrade={goToBilling}
                    adminContact={ADMIN_CONTACT}
                  >
                    <Settup openRequest={setupRequest} />
                  </SubscriptionGuard>
                )}

                {/* ── Search results ── */}
                {activeTab === "search" && (
                  <SearchResultsPage
                    currentApp={currentApp}
                    token={user?.token ?? ""}
                    onOpen={(r: SearchResult) => openSearchTarget(r.target)}
                  />
                )}

                {/* ── Header pages: notifications, account, company, help, contact ── */}
                {activeTab === "headerPages" && (
                  <HeaderPages
                    page={headerPage}
                    onNavigate={setHeaderPage}
                    onOpenSetup={() => setActiveTab("settings")}
                    onSignOut={() => {
                      const a = auth as unknown as { logout?: () => void };
                      if (a?.logout) a.logout();
                      else window.location.assign("/login");
                    }}
                  />
                )}

                {/* ── Calendar ── */}
                {activeTab === "Calender" && (
                  <SubscriptionGuard
                    moduleName="Calendar"
                    isSubscribed={hasSubscription("Calender")}
                    onGoBack={goBack}
                    onUpgrade={goToBilling}
                    adminContact={ADMIN_CONTACT}
                  >
                    <CilpronCalenders />
                  </SubscriptionGuard>
                )}

                {/* ── CRM ── */}
                {activeTab === "CRM" && (
                  <SubscriptionGuard
                    moduleName="CRM"
                    isSubscribed={hasSubscription("CRM")}
                    onGoBack={goBack}
                    onUpgrade={goToBilling}
                    adminContact={ADMIN_CONTACT}
                  >
                    <CRM />
                  </SubscriptionGuard>
                )}

                {/* ── Retail ── */}
                {activeTab === "Retail" && (
                  <SubscriptionGuard
                    moduleName="Retail"
                    isSubscribed={hasSubscription("Retail")}
                    onGoBack={goBack}
                    onUpgrade={goToBilling}
                    adminContact={ADMIN_CONTACT}
                  >
                    <Reatils />
                  </SubscriptionGuard>
                )}

                {/* ── Accounting ── */}
                {activeTab === "Accounting" && (
                  <SubscriptionGuard
                    moduleName="Accounting"
                    isSubscribed={hasSubscription("Accounting")}
                    onGoBack={goBack}
                    onUpgrade={goToBilling}
                    adminContact={ADMIN_CONTACT}
                  >
                    <Accounting />
                  </SubscriptionGuard>
                )}

                {/* ── Finance ── */}
                {activeTab === "Finance" && (
                  <SubscriptionGuard
                    moduleName="Finance"
                    isSubscribed={hasSubscription("Finance")}
                    onGoBack={goBack}
                    onUpgrade={goToBilling}
                    adminContact={ADMIN_CONTACT}
                  >
                     <FinanceAndOperation />
                  </SubscriptionGuard>
                )}

                {/* ── Task Manager ── */}
                {activeTab === "Task" && (
                  <SubscriptionGuard
                    moduleName="Task Manager"
                    isSubscribed={hasSubscription("Task")}
                    onGoBack={goBack}
                    onUpgrade={goToBilling}
                    adminContact={ADMIN_CONTACT}
                  >
                    <TaskManager />
                  </SubscriptionGuard>
                )}

                {/* ── Todo ── */}
                {activeTab === "Todo" && (
                  <SubscriptionGuard
                    moduleName="Todo"
                    isSubscribed={hasSubscription("Todo")}
                    onGoBack={goBack}
                    onUpgrade={goToBilling}
                    adminContact={ADMIN_CONTACT}
                  >
                    <div className="gU1Tf">
                      <div className="jUsfu">
                        <div className="QIl4y">{/* Todo content */}</div>
                      </div>
                    </div>
                  </SubscriptionGuard>
                )}

                {/* ── Email ── */}
                {activeTab === "email" && (
                  <SubscriptionGuard
                    moduleName="Email"
                    isSubscribed={hasSubscription("email")}
                    onGoBack={goBack}
                    onUpgrade={goToBilling}
                    adminContact={ADMIN_CONTACT}
                  >
                    <Email />
                  </SubscriptionGuard>
                )}
               {/* HYBER VIEW */}
                {activeTab === "HyperView" && (
                  <SubscriptionGuard
                    moduleName="HyperView"
                    isSubscribed={hasSubscription("HyperView")}
                    onGoBack={goBack}
                    onUpgrade={goToBilling}
                    adminContact={ADMIN_CONTACT}
                  >
                    <HyperView />
                  </SubscriptionGuard>
                )}
              {/* HYBER VIEW */}
                {/* ──FRONT DESK CONTACT CENTER CALL TRACKING  MODEL POPUP FULL SCREEN (Contacts) – matches dashboard aesthetic  ── */}
                {isFrontdeskOpen && (
                  <div className="modal-overlay">
                    <Frontdesk />
                    <button
                      type="button"
                      className="closeBtn"  
                      aria-label="Close"
                      title="Close (Esc)"
                      onClick={() => setFrontdeskIsOpen(false)}
                    >
                      <Dismiss24Regular />
                    </button>
                  </div>
                )}
                {/* ──END FRONT DESK CONTACT CENTER CALL TRACKING  MODEL POPUP FULL SCREEN (Contacts) – matches dashboard aesthetic  ── */}

                <SideBarRight handleTabClick={handleTabClick} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="Prnt6tY">
      <InternalCommunication open={support} onOpenChange={setSupport} />
    </div>

      <div>
        <AppAnalysis isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
      </div>
    </>
  );
};

export default Home;